import React, { useState, useRef, useEffect, useCallback } from "react";
import Navbar from "../components/ui/Navbar";
import ClientesPage from "./config/ClientesPage";
import AreasPage from "./config/AreasPage";
import CategoriasPage from "./config/CategoriasPage";
import MarcasModelosPage from "./config/MarcasModelosPage";
import ProveedoresPage from "./config/ProveedoresPage";
import RolesPage from "./config/RolesPage";
import ImportPage from "./config/ImportPage";
import CaracteristicasEquipoPage from "./config/CaracteristicasEquipoPage";
import AreaGruposPage from "./config/AreaGruposPage";
import DashboardPersonalizationPage from "./config/DashboardPersonalizationPage";
import TiposDocumentoPage from "./config/TiposDocumentoPage";
import SedeGruposPage from "./config/SedeGruposPage";
import ConfigCostosPage from "./config/ConfigCostosPage";
import CalibPatronesPage from "./config/CalibPatronesPage";
import CalibStickersPage from "./config/CalibStickersPage";
import ConfigReportesPage from "./config/ConfigReportesPage";
import { hasPermission } from "../utils/permissions";
import {
  Building2,
  Layers,
  MapPin,
  Tag,
  Cpu,
  Sliders,
  Truck,
  Shield,
  LayoutDashboard,
  MapPinned,
  FileText,
  Upload,
  Settings2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FlaskConical,
  Tags,
  FileCog,
} from "lucide-react";

const SECTIONS = [
  { key: "clientes",        label: "Clientes",               icon: Building2,       perm: "clientes.ver" },
  { key: "sede-grupos",     label: "Grupos de Sedes",         icon: MapPinned,       perm: "clientes.ver" },
  { key: "area-grupos",     label: "Grupos de Áreas",         icon: Layers,          perm: "areas.ver" },
  { key: "areas",           label: "Áreas",                   icon: MapPin,          perm: "areas.ver" },
  { key: "categorias",      label: "Categorías",              icon: Tag,             perm: "categorias.ver" },
  { key: "marcas",          label: "Marcas & Modelos",        icon: Cpu,             perm: "modelos.ver" },
  { key: "caracteristicas", label: "Características",         icon: Sliders,         perm: "caracteristicas.ver" },
  { key: "proveedores",     label: "Proveedores",             icon: Truck,           perm: "proveedores.ver" },
  { key: "roles",           label: "Roles & Permisos",        icon: Shield,          perm: "roles.ver" },
  { key: "dashboard",       label: "Dashboard",               icon: LayoutDashboard, perm: "dashboard.graficas" },
  { key: "tipos-documento", label: "Tipos de Documento",      icon: FileText,        perm: "equipos.editar" },
  { key: "importar",        label: "Importar",                icon: Upload,          perm: "equipos.crear" },
  { key: "costos",          label: "Configuración de Costos", icon: DollarSign,      perm: "costos_config.ver" },
  { key: "calib-patrones",  label: "Patrones de Calibración", icon: FlaskConical,    perm: "categorias.ver" },
  { key: "calib-stickers",  label: "Stickers de Calibración", icon: Tags,          perm: "categorias.ver" },
  { key: "reportes",        label: "Configuración de Reportes", icon: FileCog,     perm: "categorias.ver" },
];

const renderSection = (section) => {
  switch (section) {
    case "clientes":        return <ClientesPage />;
    case "area-grupos":     return <AreaGruposPage />;
    case "areas":           return <AreasPage />;
    case "categorias":      return <CategoriasPage />;
    case "marcas":          return <MarcasModelosPage />;
    case "proveedores":     return <ProveedoresPage />;
    case "roles":           return <RolesPage />;
    case "importar":        return <ImportPage />;
    case "caracteristicas": return <CaracteristicasEquipoPage />;
    case "dashboard":       return <DashboardPersonalizationPage />;
    case "tipos-documento": return <TiposDocumentoPage />;
    case "sede-grupos":     return <SedeGruposPage />;
    case "costos":          return <ConfigCostosPage embedded />;
    case "calib-patrones":  return <CalibPatronesPage />;
    case "calib-stickers":  return <CalibStickersPage />;
    case "reportes":        return <ConfigReportesPage />;
    default:                return <ClientesPage />;
  }
};

const ConfigPage = () => {
  const visible = SECTIONS.filter((s) => !s.perm || hasPermission(s.perm));
  const [section, setSection] = useState(visible[0]?.key ?? "clientes");
  const navRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scroll = (dir) => {
    navRef.current?.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-600 rounded-xl">
            <Settings2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
            <p className="text-sm text-gray-500">
              Administra clientes, equipos, roles y preferencias del sistema
            </p>
          </div>
        </div>

        {/* Tab nav */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
          <div className="relative flex items-center">
            {/* Left arrow */}
            <button
              onClick={() => scroll(-1)}
              className={`absolute left-0 z-10 h-full px-2 flex items-center bg-gradient-to-r from-white via-white to-transparent transition-opacity ${
                canScrollLeft ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
              }`}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </span>
            </button>

            <nav
              ref={navRef}
              className="flex overflow-x-auto scrollbar-hide px-2 py-2 gap-1 w-full"
            >
              {visible.map(({ key, label, icon: Icon }) => {
                const active = section === key;
                return (
                  <button
                    key={key}
                    onClick={() => setSection(key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                      active
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${active ? "text-white" : "text-gray-400"}`} />
                    {label}
                  </button>
                );
              })}
            </nav>

            {/* Right arrow */}
            <button
              onClick={() => scroll(1)}
              className={`absolute right-0 z-10 h-full px-2 flex items-center bg-gradient-to-l from-white via-white to-transparent transition-opacity ${
                canScrollRight ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
              }`}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors">
                <ChevronRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          {renderSection(section)}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ConfigPage;
